import { Component, EventEmitter, Output } from '@angular/core';
import { FilterStateService } from '../../services/filter-state.service';
import { BASIC_IMPORTS } from '../../imports/common-imports';

@Component({
  selector: 'app-filter-visualizer',
  standalone: true,
  imports: [BASIC_IMPORTS],
  templateUrl: './filter-visualizer.component.html',
  styleUrl: './filter-visualizer.component.scss',
})
export class FilterVisualizerComponent {
  @Output() filterCleared = new EventEmitter<string>();

  filterData: any;
  pristineFilters: any; // To store the original state of filters

  constructor(private readonly filterStateService: FilterStateService) {}

  ngOnInit() {
    this.loadFilterData();
  }

  // Utility: Deep copy using structuredClone or fallback to JSON
  private deepCopy<T>(obj: T): T {
    if (typeof structuredClone === 'function') {
      return structuredClone(obj);
    }
    return JSON.parse(JSON.stringify(obj));
  }

  loadFilterData() {
    this.filterData = this.filterStateService.filterData;
    this.pristineFilters = this.deepCopy(
      this.filterStateService.pristineFilters,
    );
  }

  /**
   * Checks if a filter key is actively applied using the key-based tracking
   */
  isFilterApplied(key: string): boolean {
    return this.filterStateService.isFilterApplied(key);
  }

  /**
   * Clears a specific filter by key and restores pristine values
   */
  clearFilter(key: string) {
    // Clear the applied filter key
    this.filterStateService.clearFilter(key);

    // Restore pristine value for the filter
    const pristineValue = this.deepCopy(this.pristineFilters[key]);
    this.filterData[key] = pristineValue;
    (this.filterStateService.filterData as any)[key] = pristineValue;

    // Handle edge case: if regions cleared and no markets, restore regions
    if (
      key === 'regions' &&
      this.filterStateService.filterData.markets.length === 0
    ) {
      this.filterStateService.filterData.regions =
        this.filterStateService.pristineFilters.regions;
    }

    // Handle edge case: if markets cleared and no regions, restore regions
    if (
      key === 'markets' &&
      this.filterStateService.filterData.regions.length === 0
    ) {
      this.filterStateService.filterData.regions =
        this.filterStateService.pristineFilters.regions;
    }

    this.filterCleared.emit(key);
  }

  /**
   * Clears date range filters
   */
  clearDateFilters() {
    const sessionCacheKey = 'monthly-selected-range';
    sessionStorage.removeItem(sessionCacheKey);

    // Clear the applied filter key
    this.filterStateService.clearFilter('date_range');

    // Restore pristine date values
    const pristineStartMonth = this.deepCopy(this.pristineFilters.start_month);
    const pristineEndMonth = this.deepCopy(this.pristineFilters.end_month);

    this.filterData.start_month = pristineStartMonth;
    this.filterData.end_month = pristineEndMonth;
    this.filterStateService.filterData.start_month = pristineStartMonth;
    this.filterStateService.filterData.end_month = pristineEndMonth;

    this.filterCleared.emit('date_range');
  }

  /**
   * Checks if any filter is currently applied
   */
  hasAnyChange(): boolean {
    return this.filterStateService.hasAnyFilterApplied();
  }
}
